import type { OnboardingInviteRecipient } from 'src/features/advisor-book-onboarding/api/onboarding-cockpit-gql';
import { isNil } from 'es-toolkit';
import { toast } from 'sonner';
import { RESEND_ADVISOR_ONBOARDING_MUTATION } from 'src/features/advisor-book-onboarding/api/send-onboarding-gql';
import { ResendInviteRecipientPicker } from 'src/features/advisor-book-onboarding/components/resend-invite-recipient-picker';
import { useConfirmationDialog } from 'src/hooks/use-confirmation-dialog';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

type ResendOnboardingInviteArgs = {
  readonly stagedHouseholdId: string;
  readonly householdName: string;
  readonly recipients?: readonly OnboardingInviteRecipient[];
  readonly primaryClientName?: string | null;
  readonly primaryClientEmail?: string | null;
  /** Runs after a successful resend (e.g. the cockpit table refetches its list). */
  readonly onSuccess?: () => unknown;
};

function recipientLabel(recipient: OnboardingInviteRecipient) {
  const name = recipient.name ?? recipient.email ?? 'Client';
  return isNil(recipient.email) ? name : `${name} (${recipient.email})`;
}

// Shared by the cockpit table row action and the detail sheet so the two surfaces stay in sync.
export function useResendOnboardingInvite() {
  const [resendInvite] = useMutationWithErrorLogging(RESEND_ADVISOR_ONBOARDING_MUTATION);
  const showConfirmation = useConfirmationDialog();

  function confirmResendInvite({
    stagedHouseholdId,
    householdName,
    recipients = [],
    primaryClientName,
    primaryClientEmail,
    onSuccess,
  }: ResendOnboardingInviteArgs) {
    const eligible = recipients.filter((recipient) => recipient.canResend);

    async function send(clientOnboardingRequestIds: readonly string[] | undefined) {
      try {
        const { data } = await resendInvite({
          variables: {
            stagedHouseholdId,
            clientOnboardingRequestIds: isNil(clientOnboardingRequestIds) ? null : [...clientOnboardingRequestIds],
          },
        });
        const payload = data?.resendAdvisorOnboarding;
        if (payload?.success !== true) {
          toast.error(payload?.errors?.[0]?.message ?? 'Could not resend the invite. Please try again.');
          return;
        }

        const sentTo = payload.resentTo ?? [];
        const names = sentTo.map((recipient) => recipient.name ?? recipient.email ?? 'client');
        toast.success(
          names.length > 0
            ? `A new onboarding invite was sent to ${names.join(' and ')}.`
            : `A new onboarding invite was sent to ${householdName}.`,
        );

        const skipped = payload.skipped ?? [];
        if (skipped.length > 0) {
          const skippedNames = skipped.map((recipient) => recipient.name ?? 'A client').join(' and ');
          toast.warning(`${skippedNames} was not sent an invite.`);
        }

        await onSuccess?.();
      } catch (err: unknown) {
        handleMutationError(err, 'Could not resend the invite. Please try again.');
      }
    }

    if (eligible.length < 2) {
      const fallbackName = isNil(primaryClientName) || primaryClientName === '' ? householdName : primaryClientName;
      const hasFallbackEmail = !isNil(primaryClientEmail) && primaryClientEmail !== '';
      const fallbackLabel = hasFallbackEmail ? `${fallbackName} (${primaryClientEmail})` : fallbackName;
      const only = eligible[0];
      const recipient = isNil(only) ? fallbackLabel : recipientLabel(only);

      showConfirmation({
        title: 'Resend invite email?',
        description: `This onboarding invite was already sent to ${recipient}. Resending emails a new link and expires the current one.`,
        cancelLabel: 'Cancel',
        confirmationLabel: 'Resend invite',
        onConfirmation: () => send(undefined),
      });
      return;
    }

    // Mutated by the picker so this handler reads the live selection; seeded with everyone eligible.
    const selection = new Set(eligible.map((recipient) => recipient.id));

    showConfirmation({
      title: 'Resend invite email?',
      description: <ResendInviteRecipientPicker recipients={recipients} selection={selection} />,
      cancelLabel: 'Cancel',
      confirmationLabel: 'Resend invite',
      onConfirmation: () => {
        if (selection.size === 0) {
          toast.error('Select at least one person to resend to.');
          return;
        }
        return send([...selection]);
      },
    });
  }

  return { confirmResendInvite };
}
